/**
 * MEDIEVAL CASTLE ARCHITECTURE — pure layout (no three.js) for
 * mapExtras/medieval_castle.ts, checked by agent/bench-prop-swaps.mjs.
 *
 * Owner rule (2026-11-04): castle architecture is NOT imported; real-castle
 * references (Sketchfab "Medieval castle" kits, curtain-wall photos) were used
 * only for proportions. Everything here is built from the shipped collider
 * boxes, found by shape, so it follows maps.ts edits:
 *   - MACHICOLATION CORBELS: three-step stone brackets under the overhang of
 *     every wall-walk slab and tower-top slab. They sit entirely UNDER the
 *     slab's overhang (top flush with its underside), so they add relief
 *     without inventing a ledge;
 *   - ARROW SLITS (cross loops) with a cut-stone surround on the outer faces
 *     of the curtain walls and towers, ≤ 0.1 m proud;
 *   - MERLON COPING: a weathering cap on every merlon, 0.08 m proud;
 *   - BANNERS hanging from the tower tops and the gatehouse, ≤ 0.2 m proud;
 *   - TORCH SCONCES either side of the gate arch, ≤ 0.26 m proud.
 */
import type { MapBox, MapDef } from "@neon-purge/shared";

export interface LBox { p: [number, number, number]; s: [number, number, number] }
export interface Banner { c: [number, number, number]; w: number; h: number; nx: number; nz: number }
export interface CastleDressing {
  stone: LBox[];      // corbels, slit surrounds, coping
  slits: LBox[];      // the dark openings
  iron: LBox[];       // sconce brackets + torch hafts
  flames: LBox[];     // torch heads (emissive)
  banners: Banner[];
  /** corbels, for the bench (must sit under a slab overhang) */
  corbels: LBox[];
}

const STONE = "#77736a", STONE_DARK = "#57554f", STONE_LIGHT = "#9d978c";
const col = (b: MapBox): string => (b.color ?? "").toLowerCase();
const near = (a: number, b: number, e = 0.05): boolean => Math.abs(a - b) < e;

export const PROUD_SLIT = 0.1;

export function castleDressing(map: MapDef, lite: boolean): CastleDressing {
  const out: CastleDressing = { stone: [], slits: [], iron: [], flames: [], banners: [], corbels: [] };
  const B = map.boxes;
  const walls = B.filter((b) => col(b) === STONE && near(b.s[1], 10) && near(b.p[1], 5) && (near(b.s[0], 4) || near(b.s[2], 4)));
  const walks = B.filter((b) => col(b) === STONE_LIGHT && near(b.s[1], 0.6) && near(b.p[1], 9.1));
  const towers = B.filter((b) => col(b) === STONE_DARK && near(b.s[0], 13) && near(b.s[1], 14) && near(b.s[2], 13));
  const towerTops = B.filter((b) => col(b) === STONE_LIGHT && near(b.s[0], 14) && near(b.s[1], 0.6));
  const merlons = B.filter((b) => col(b) === STONE_DARK && near(b.s[1], 2.4) && near(b.p[1], 11));
  const gate = B.filter((b) => col(b) === STONE_DARK && near(b.s[0], 10) && near(b.s[1], 16) && near(b.s[2], 9));

  /** Corbels under a slab overhang along one face. axis 0: face normal ±x. */
  const corbelRow = (slab: MapBox, wallHalf: number, ax: 0 | 2, sign: number, from: number, to: number, step: number): void => {
    const over = slab.s[ax] / 2 - wallHalf;           // overhang depth
    if (over < 0.3) return;
    const under = slab.p[1] - slab.s[1] / 2;
    const face = slab.p[ax] + sign * wallHalf;
    const along = ax === 0 ? 2 : 0;
    // three courses, deepest on top (flush with the slab underside)
    const steps: [number, number][] = [[over - 0.03, 0.26], [over * 0.62, 0.24], [over * 0.3, 0.24]];
    for (let t = from + step / 2; t < to - step / 4; t += step) {
      let y = under;
      for (const [depth, h] of steps) {
        const p: [number, number, number] = [0, y - h / 2, 0];
        const s: [number, number, number] = [0, h, 0];
        p[ax] = face + sign * depth / 2; s[ax] = depth;
        p[along] = t; s[along] = 0.55;
        const bx = { p, s };
        out.stone.push(bx); out.corbels.push(bx);
        y -= h;
      }
    }
  };
  /** A cross-loop on a face: dark slit + cut-stone surround. */
  const slit = (ax: 0 | 2, face: number, sign: number, t: number, yc: number): void => {
    const along = ax === 0 ? 2 : 0;
    const mk = (dt: number, dy: number, w: number, h: number, proud: number, list: LBox[]): void => {
      const p: [number, number, number] = [0, yc + dy, 0];
      const s: [number, number, number] = [0, h, 0];
      p[ax] = face + sign * proud / 2; s[ax] = proud;
      p[along] = t + dt; s[along] = w;
      list.push({ p, s });
    };
    mk(0, 0, 0.16, 1.9, 0.03, out.slits);            // the loop
    mk(0, 0.25, 0.62, 0.12, 0.03, out.slits);        // cross arm
    mk(-0.19, 0, 0.2, 2.3, PROUD_SLIT, out.stone);   // jambs
    mk(0.19, 0, 0.2, 2.3, PROUD_SLIT, out.stone);
    mk(0, 1.22, 0.78, 0.2, PROUD_SLIT, out.stone);   // lintel
    mk(0, -1.22, 0.78, 0.2, PROUD_SLIT, out.stone);  // sill
  };
  const inTower = (x: number, z: number): boolean => towers.some((t) => Math.abs(x - t.p[0]) < t.s[0] / 2 + 1.5 && Math.abs(z - t.p[2]) < t.s[2] / 2 + 1.5);
  const inGate = (x: number, z: number): boolean => gate.some((g) => Math.abs(x - g.p[0]) < g.s[0] / 2 + 1.5 && Math.abs(z - g.p[2]) < g.s[2] / 2 + 1.5);

  for (const w of walls) {
    const ax: 0 | 2 = near(w.s[0], 4) ? 0 : 2;   // thin axis = face normal
    const along = ax === 0 ? 2 : 0;
    const half = w.s[ax] / 2;
    const outSign = Math.sign(w.p[ax]) || 1;      // away from the keep
    const lo = w.p[along] - w.s[along] / 2, hi = w.p[along] + w.s[along] / 2;
    const walk = walks.find((s) => near(s.p[ax], w.p[ax], 0.2) && Math.abs(s.p[along] - w.p[along]) < 1);
    if (walk) for (const sg of lite ? [outSign] : [outSign, -outSign]) corbelRow(walk, half, ax, sg, lo, hi, 2.0);
    // loops halfway between merlons (merlon centres every 8 m from ±44)
    for (let t = -40; t <= 40; t += 8) {
      if (t < lo + 1.5 || t > hi - 1.5) continue;
      const x = ax === 0 ? w.p[0] : t, z = ax === 0 ? t : w.p[2];
      if (inTower(x, z) || inGate(x, z)) continue;
      slit(ax, w.p[ax] + outSign * half, outSign, t, 5.2);
    }
  }
  for (const t of towers) {
    const top = towerTops.find((s) => near(s.p[0], t.p[0], 0.2) && near(s.p[2], t.p[2], 0.2));
    for (const ax of [0, 2] as const) for (const sg of [-1, 1]) {
      const along = ax === 0 ? 2 : 0;
      const outward = Math.sign(t.p[ax]) === sg;
      if (top && (outward || !lite)) corbelRow(top, t.s[ax] / 2, ax, sg, t.p[along] - t.s[along] / 2 + 0.3, t.p[along] + t.s[along] / 2 - 0.3, 1.6);
      if (!outward) continue;
      const face = t.p[ax] + sg * t.s[ax] / 2;
      for (const dy of [4.6, 9.4]) for (const dt of [-3.2, 3.2]) slit(ax, face, sg, t.p[along] + dt, dy);
      // banner centred on the outer face, hanging from under the corbel table
      const c: [number, number, number] = [t.p[0], 0, t.p[2]];
      c[ax] = face + sg * 0.12;
      c[1] = 12.9 - 5.6 / 2;
      out.banners.push({ c, w: 2.4, h: 5.6, nx: ax === 0 ? sg : 0, nz: ax === 2 ? sg : 0 });
    }
  }
  for (const m of merlons) out.stone.push({ p: [m.p[0], m.p[1] + m.s[1] / 2 + 0.07, m.p[2]], s: [m.s[0] + 0.16, 0.14, m.s[2] + 0.16] });
  // gatehouse: a banner on each tower's outer (north) face + torch sconces at the arch
  for (const g of gate) {
    const sg = Math.sign(g.p[2]) || -1;
    const face = g.p[2] + sg * g.s[2] / 2;
    out.banners.push({ c: [g.p[0], 9.5, face + sg * 0.12], w: 2.6, h: 6.2, nx: 0, nz: sg });
    const ax = g.p[0] + (g.p[0] > 0 ? -1 : 1) * (g.s[0] / 2 - 0.9); // just beside the arch
    out.iron.push({ p: [ax, 3.4, face + sg * 0.12], s: [0.1, 0.5, 0.24] });     // wall plate + arm
    out.iron.push({ p: [ax, 3.75, face + sg * 0.17], s: [0.07, 0.7, 0.07] });   // haft
    out.flames.push({ p: [ax, 4.22, face + sg * 0.17], s: [0.16, 0.26, 0.16] });
    for (const dt of [-2.6, 2.6]) slit(2, face, sg, g.p[0] + dt, 11);
  }
  return out;
}
